
import { useState, useEffect } from 'react';
import { useResellerPlans } from './license/useResellerPlans';
import { useToast } from './use-toast';
import type { ResellerPlanDetails } from './license/resellerPlans/types';
import { inviteSuperAdminManager } from '@/utils/adminUserService';

export const useSuperAdminProfile = () => {
  const { resellerPlans, isLoadingPlans } = useResellerPlans();
  const [isLoading, setIsLoading] = useState(true);
  const [currentPlan, setCurrentPlan] = useState<ResellerPlanDetails | null>(null);
  const [isUpgrading, setIsUpgrading] = useState(false);
  const [superAdminProfile, setSuperAdminProfile] = useState({
    id: 'sa-1',
    name: 'Super Admin',
    email: 'superadmin@example.com',
    planId: '2', // Corresponds to Professional plan in mock data
    instances: 3,
    managerCount: 2,
    createdAt: new Date('2023-01-15')
  });
  
  const remainingInstances = currentPlan ? (currentPlan.maxInstances - superAdminProfile.instances) : 0;
  const remainingAdmins = currentPlan && currentPlan.maxManagers ? (currentPlan.maxManagers - superAdminProfile.managerCount) : 0;
  
  useEffect(() => {
    const loadProfile = async () => {
      setIsLoading(true);
      try {
        await new Promise(resolve => setTimeout(resolve, 800));
        
        if (!isLoadingPlans && resellerPlans.length > 0) {
          const plan = resellerPlans.find(p => p.id === superAdminProfile.planId) || null;
          setCurrentPlan(plan);
        }
      } catch (error) {
        console.error('Error loading super admin profile:', error);
      } finally {
        setIsLoading(false);
      }
    };
    
    loadProfile();
  }, [isLoadingPlans, resellerPlans, superAdminProfile.planId]);
  
  const handleUpgradePlan = async (planId: string) => {
    try {
      setIsUpgrading(true);
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      setSuperAdminProfile(prev => ({
        ...prev,
        planId
      }));
      
      return true;
    } catch (error) {
      console.error('Error upgrading plan:', error);
      return false;
    } finally {
      setIsUpgrading(false);
    }
  };
  
  const handleInviteSuperAdmin = async (data: { 
    name: string; 
    email: string; 
    phone: string; 
  }): Promise<boolean> => {
    try {
      console.log('Inviting super admin with data:', data);
      
      // Check if we have remaining slots before trying to invite
      if (remainingAdmins <= 0) {
        console.error('No remaining admin slots');
        return false;
      }
      
      // Call the adminUserService function
      const success = await inviteSuperAdminManager(data);
      
      if (success) {
        // Update the profile to increment manager count
        setSuperAdminProfile(prev => ({
          ...prev,
          managerCount: prev.managerCount + 1
        }));
        
        console.log('Super admin invited successfully');
        return true;
      }
      
      return false;
    } catch (error) {
      console.error('Error inviting super admin:', error);
      return false;
    }
  };
  
  return {
    superAdminProfile,
    isLoading: isLoading || isLoadingPlans,
    currentPlan,
    resellerPlans,
    isLoadingPlans,
    handleUpgradePlan,
    remainingInstances,
    remainingAdmins,
    isUpgrading,
    handleInviteSuperAdmin
  };
};
